import type { MouseEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { COMMUNITY_SWITCHER_NAV_SELECTOR } from "../constants";

const isExternalLink = (href: string) => {
  try {
    const linkUrl = new URL(href, globalThis.location.origin);
    return linkUrl.origin !== globalThis.location.origin;
  } catch {
    return false;
  }
};

interface UseNavigationLoadingStateProps {
  currentCommunityId?: number;
  onCommunitySwitch?: () => void;
  timeoutMs: number;
}

export const useNavigationLoadingState = ({
  currentCommunityId,
  onCommunitySwitch,
  timeoutMs,
}: UseNavigationLoadingStateProps) => {
  const [clickedCommunityId, setClickedCommunityId] = useState<number | null>(
    null,
  );
  const navigationTimeout = useRef<number | null>(null);

  const handleNavClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const navTarget = event.target.closest(COMMUNITY_SWITCHER_NAV_SELECTOR);
      if (!(navTarget instanceof HTMLAnchorElement)) {
        return;
      }

      const href = navTarget.getAttribute("href");
      if (!href) {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      // Skip when modifier keys open the link in a new tab/window — the current
      // page isn't navigating, so flipping the active community would mislead.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }

      const communityIdAttr = navTarget.getAttribute("data-community-id");
      const communityId = communityIdAttr ? Number(communityIdAttr) : NaN;
      if (!Number.isNaN(communityId)) {
        setClickedCommunityId(communityId);
        if (communityId !== currentCommunityId) onCommunitySwitch?.();
      }

      if (!isExternalLink(href)) {
        if (navigationTimeout.current !== null) {
          globalThis.clearTimeout(navigationTimeout.current);
        }
        navigationTimeout.current = globalThis.setTimeout(() => {
          setClickedCommunityId(null);
        }, timeoutMs);
      }
    },
    [currentCommunityId, onCommunitySwitch, timeoutMs],
  );

  useEffect(
    () => () => {
      if (navigationTimeout.current === null) {
        return;
      }
      setClickedCommunityId(null);
      globalThis.clearTimeout(navigationTimeout.current);
      navigationTimeout.current = null;
    },
    [],
  );

  return { handleNavClick, clickedCommunityId };
};
